import React, { useState, useEffect } from 'react';
import { FletesService } from '../services/fletesService';
import { ChoferesService } from '../services/choferesService';
import FormularioFletes from './FormularioFletes';
import { FiDownload } from 'react-icons/fi';
import './GestionFletes.css';

const GestionFletes = () => {
  const [fletes, setFletes] = useState([]);
  const [choferes, setChoferes] = useState([]);
  const [mostrarFormulario, setMostrarFormulario] = useState(false);
  const [fleteEditar, setFleteEditar] = useState(null);
  const [filtros, setFiltros] = useState({
    fecha: '',
    chofer: '',
    estatus: 'todos'
  });

  useEffect(() => {
    const unsubscribeFletes = FletesService.suscribirseFletes(setFletes);
    const unsubscribeChoferes = ChoferesService.suscribirseChoferes(setChoferes);

    return () => {
      if (typeof unsubscribeFletes === 'function') unsubscribeFletes();
      if (typeof unsubscribeChoferes === 'function') unsubscribeChoferes();
    };
  }, []);

  const handleEliminarFlete = async (fleteId) => {
    if (!window.confirm('¿Deseas eliminar este flete?')) return;
    try {
      await FletesService.eliminarFlete(fleteId);
      alert('Flete eliminado correctamente');
    } catch (error) {
      console.error('Error eliminando flete:', error);
      alert('No se pudo eliminar el flete');
    }
  };

  const handleEditarFlete = (flete) => {
    setFleteEditar(flete);
    setMostrarFormulario(true);
  };

  const handleCancelarFormulario = () => {
    setMostrarFormulario(false);
    setFleteEditar(null);
  };

  const formatDate = (dateString) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES');
  };

  const formatCurrency = (amount) => {
    if (!amount) return '$0.00';
    return new Intl.NumberFormat('es-MX', {
      style: 'currency',
      currency: 'MXN'
    }).format(amount);
  };

  const getStatusClass = (estatus) => {
    const statusMap = {
      'Pendiente': 'status-pendiente',
      'En tránsito': 'status-en-transito',
      'Entregado': 'status-entregado',
      'Cancelado': 'status-cancelado'
    };
    return statusMap[estatus] || 'status-default';
  };

  // Filtrar fletes
  const fletesFiltrados = fletes.filter(flete => {
    if (filtros.fecha && flete.fecha !== filtros.fecha) return false;
    if (filtros.chofer && flete.chofer !== filtros.chofer) return false;
    if (filtros.estatus !== 'todos' && flete.estatus !== filtros.estatus) return false;
    return true;
  }).sort((a, b) => {
    const fechaA = new Date(a.fecha || 0).getTime();
    const fechaB = new Date(b.fecha || 0).getTime();
    return fechaB - fechaA;
  });

  // Calcular estadísticas
  const estadisticas = {
    total: fletes.length,
    pendientes: fletes.filter(f => f.estatus === 'Pendiente').length,
    enTransito: fletes.filter(f => f.estatus === 'En tránsito').length,
    entregados: fletes.filter(f => f.estatus === 'Entregado').length,
    cancelados: fletes.filter(f => f.estatus === 'Cancelado').length,
    totalMonto: fletes.reduce((sum, f) => sum + (parseFloat(f.monto) || 0), 0),
    fletesPagados: fletes.filter(f => f.fletePagado === 'Sí').length
  };

  // Función para exportar reporte de fletes a Excel
  const exportarReporteExcel = () => {
    // Agrupar fletes por chofer
    const fletesPorChofer = {};
    
    fletesFiltrados.forEach(flete => {
      const chofer = flete.chofer || 'Sin asignar';
      const numeroFlete = flete.id ? `FL-${flete.id.slice(-6)}` : `FL-${Date.now()}`;
      
      if (!fletesPorChofer[chofer]) {
        fletesPorChofer[chofer] = {
          nombre: chofer,
          fletes: []
        };
      }
      
      fletesPorChofer[chofer].fletes.push({
        numeroFlete,
        fecha: formatDate(flete.fecha),
        destinoInicial: flete.destinoInicial || '',
        destinoFinal: flete.destinoFinal || '',
        monto: flete.monto || 0,
        estatus: flete.estatus || ''
      });
    });

    // Calcular total de fletes
    const totalFletes = Object.values(fletesPorChofer).reduce(
      (sum, c) => sum + c.fletes.length, 0
    );

    const fechaActual = new Date().toLocaleDateString('es-MX', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });

    // Generar HTML con estilos para Excel
    let htmlContent = `
      <html xmlns:x="urn:schemas-microsoft-com:office:excel">
      <head>
        <meta charset="UTF-8">
        <style>
          table { border-collapse: collapse; width: 100%; font-family: Arial, sans-serif; }
          td, th { border: 1px solid #000; padding: 8px; text-align: center; }
          .titulo { background-color: #4472C4; color: white; font-size: 16px; font-weight: bold; }
          .fecha { font-style: italic; font-size: 11px; text-align: left; }
          .header-morado { background-color: #7030A0; color: white; font-weight: bold; font-size: 13px; }
          .header-azul { background-color: #00B0F0; color: white; font-weight: bold; font-size: 12px; }
          .total-azul { background-color: #00B0F0; color: white; font-weight: bold; border-top: 2px solid #000; border-bottom: 2px solid #000; }
          .dato { background-color: #FFFFFF; }
          .dato-alternado { background-color: #F2F2F2; }
        </style>
      </head>
      <body>
        <table>
          <!-- Título Principal -->
          <tr>
            <td colspan="2" class="titulo">Reporte de Fletes - MADERERÍA LA HUERTA</td>
          </tr>
          <!-- Fecha de Generación -->
          <tr>
            <td colspan="2" class="fecha">Fecha de generación: ${fechaActual}</td>
          </tr>
          <!-- Espacio -->
          <tr><td colspan="2">&nbsp;</td></tr>
          
          <!-- RESUMEN GENERAL -->
          <tr>
            <td colspan="2" class="header-morado">RESUMEN GENERAL</td>
          </tr>
          <tr>
            <th class="header-morado">Chofer</th>
            <th class="header-morado">Total Fletes</th>
          </tr>`;
    
    // Agregar datos de choferes en el resumen
    Object.values(fletesPorChofer).forEach((chofer, index) => {
      const totalFletesChofer = chofer.fletes.length;
      const rowClass = index % 2 === 0 ? 'dato' : 'dato-alternado';
      htmlContent += `
          <tr>
            <td class="${rowClass}">${chofer.nombre}</td>
            <td class="${rowClass}">${totalFletesChofer}</td>
          </tr>`;
    });
    
    // Fila de total
    htmlContent += `
          <tr>
            <td class="total-azul">TOTAL:</td>
            <td class="total-azul">${totalFletes}</td>
          </tr>
          <tr><td colspan="2">&nbsp;</td></tr>`;
    
    // Detalle por chofer
    Object.values(fletesPorChofer).forEach(chofer => {
      htmlContent += `
          <!-- Detalle de ${chofer.nombre} -->
          <tr>
            <td colspan="2" class="header-azul">Detalle de ${chofer.nombre.toUpperCase()}</td>
          </tr>
          <tr>
            <th class="header-morado">Número de Documento</th>
            <th class="header-morado">Entregas</th>
          </tr>`;
      
      // Agregar cada flete del chofer
      chofer.fletes.forEach((flete, index) => {
        const rowClass = index % 2 === 0 ? 'dato' : 'dato-alternado';
        htmlContent += `
          <tr>
            <td class="${rowClass}">${flete.numeroFlete}</td>
            <td class="${rowClass}">1</td>
          </tr>`;
      });
      
      // Total del chofer
      htmlContent += `
          <tr>
            <td class="total-azul">TOTAL ${chofer.nombre.toUpperCase()}:</td>
            <td class="total-azul">${chofer.fletes.length}</td>
          </tr>
          <tr><td colspan="2">&nbsp;</td></tr>`;
    });
    
    htmlContent += `
        </table>
      </body>
      </html>`;
    
    // Crear blob y descargar
    const blob = new Blob([htmlContent], { type: 'application/vnd.ms-excel' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    const fechaArchivo = new Date().toISOString().slice(0, 10).replace(/-/g, '');
    link.download = `reporte_fletes_${fechaArchivo}.xls`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  return (
    <div className="gestion-fletes">
      {/* Header */}
      <div className="header-section-fletes">
        <h1>Gestión de Fletes</h1>
        <button 
          className="btn-registrar-flete"
          onClick={() => {
            setFleteEditar(null);
            setMostrarFormulario(true);
          }}
        >
          + Registrar Flete
        </button>
      </div>

      {/* Estadísticas */}
      <div className="estadisticas-fletes">
        <div className="stat-card-flete">
          <div className="stat-label-flete">Total Fletes</div>
          <div className="stat-value-flete">{estadisticas.total}</div>
        </div>
        <div className="stat-card-flete">
          <div className="stat-label-flete">Pendientes</div>
          <div className="stat-value-flete stat-pendiente">{estadisticas.pendientes}</div>
        </div>
        <div className="stat-card-flete">
          <div className="stat-label-flete">En Tránsito</div>
          <div className="stat-value-flete stat-en-transito">{estadisticas.enTransito}</div>
        </div>
        <div className="stat-card-flete">
          <div className="stat-label-flete">Entregados</div>
          <div className="stat-value-flete stat-entregado">{estadisticas.entregados}</div>
        </div>
        <div className="stat-card-flete">
          <div className="stat-label-flete">Monto Total</div>
          <div className="stat-value-flete stat-monto">{formatCurrency(estadisticas.totalMonto)}</div>
        </div>
        <div className="stat-card-flete">
          <div className="stat-label-flete">Fletes Pagados</div>
          <div className="stat-value-flete stat-pagado">{estadisticas.fletesPagados}</div>
        </div>
      </div>

      {/* Filtros */}
      <div className="filtros-section-fletes">
        <h2>Filtros de Búsqueda</h2>
        <div className="filtros-grid-fletes">
          <div className="filter-group-flete">
            <label>Fecha:</label>
            <input
              type="date"
              value={filtros.fecha}
              onChange={(e) => setFiltros({...filtros, fecha: e.target.value})}
            />
          </div>
          <div className="filter-group-flete">
            <label>Chofer:</label>
            <select
              value={filtros.chofer}
              onChange={(e) => setFiltros({...filtros, chofer: e.target.value})}
            >
              <option value="">Todos los choferes</option>
              {choferes.map(chofer => (
                <option key={chofer.id} value={`${chofer.nombre} ${chofer.apellido || ''}`.trim()}>
                  {chofer.nombre} {chofer.apellido || ''}
                </option>
              ))}
            </select>
          </div>
          <div className="filter-group-flete">
            <label>Estatus:</label>
            <select
              value={filtros.estatus}
              onChange={(e) => setFiltros({...filtros, estatus: e.target.value})}
            >
              <option value="todos">Todos los estatus</option>
              <option value="Pendiente">Pendiente</option>
              <option value="En tránsito">En tránsito</option>
              <option value="Entregado">Entregado</option>
              <option value="Cancelado">Cancelado</option>
            </select>
          </div>
          <div className="filter-group-flete">
            <button 
              className="btn-limpiar-filtros"
              onClick={() => setFiltros({ fecha: '', chofer: '', estatus: 'todos' })}
            >
              Limpiar Filtros
            </button>
          </div>
        </div>
      </div>

      {/* Tabla de Fletes */}
      <div className="tabla-section-fletes">
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1.5rem' }}>
          <h2 style={{ margin: 0 }}>Registro de Fletes ({fletesFiltrados.length})</h2>
          <button 
            className="btn-exportar-excel-fletes"
            onClick={exportarReporteExcel}
            title="Exportar a Excel"
          >
            <FiDownload /> Exportar a Excel
          </button>
        </div>
        {fletesFiltrados.length === 0 ? (
          <div className="no-data-fletes">
            <p>No hay fletes registrados</p>
          </div>
        ) : (
          <div className="table-container-fletes">
            <table className="fletes-table">
              <thead>
                <tr>
                  <th>Fecha</th>
                  <th>Destino Inicial</th>
                  <th>Destino Final</th>
                  <th>Chofer</th>
                  <th>Estatus</th>
                  <th>Monto</th>
                  <th>Flete Pagado</th>
                  <th>Tipo Unidad</th>
                  <th>Confirmado Por</th>
                  <th>Acciones</th>
                </tr>
              </thead>
              <tbody>
                {fletesFiltrados.map((flete) => (
                  <tr key={flete.id}>
                    <td>{formatDate(flete.fecha)}</td>
                    <td>{flete.destinoInicial || '-'}</td>
                    <td>{flete.destinoFinal || '-'}</td>
                    <td>{flete.chofer || '-'}</td>
                    <td>
                      <span className={`status-badge-flete ${getStatusClass(flete.estatus)}`}>
                        {flete.estatus || '-'}
                      </span>
                    </td>
                    <td>{formatCurrency(flete.monto)}</td>
                    <td>
                      <span className={flete.fletePagado === 'Sí' ? 'pagado' : 'no-pagado'}>
                        {flete.fletePagado || 'No'}
                      </span>
                    </td>
                    <td>{flete.tipoUnidad || '-'}</td>
                    <td>{flete.confirmadoPor || '-'}</td>
                    <td className="acciones-fletes">
                      <button 
                        className="btn-editar-flete"
                        onClick={() => handleEditarFlete(flete)}
                      >
                        Editar
                      </button>
                      <button 
                        className="btn-eliminar-flete"
                        onClick={() => handleEliminarFlete(flete.id)}
                      >
                        Eliminar
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Formulario Modal */}
      {mostrarFormulario && (
        <FormularioFletes 
          onCancel={handleCancelarFormulario}
          fleteEditar={fleteEditar}
        />
      )}
    </div>
  );
};

export default GestionFletes;

